import React, { useCallback, useState } from "react";
import { updateDataset } from "@/lib/api/datasets";
import { DescriptionSection } from "@/components/main/datasets/DescriptionSection";
import { showNotificationGlobal } from "@/stores/useNotificationStore";

interface DatasetTextMetaProps {
  datasetId: string;
  /// Which dataset column this field maps to.
  field: "license" | "attribution";
  /// The persisted value (owned by the parent row).
  value: string | null;
  label: string;
  icon: React.ReactNode;
  addLabel: string;
  placeholder: string;
  canEdit: boolean;
  /// Bubble the saved value up so the row keeps the single latest copy.
  onSaved: (value: string | null) => void;
  pr?: string;
}

/// A single free-text dataset metadata field (License / Attribution), backed by
/// the datasets PATCH API and rendered with the shared `DescriptionSection`
/// shell. Mirrors `DatasetDescription`, parameterized by the target column.
export const DatasetTextMeta: React.FC<DatasetTextMetaProps> = ({
  datasetId,
  field,
  value,
  label,
  icon,
  addLabel,
  placeholder,
  canEdit,
  onSaved,
  pr,
}) => {
  const [status, setStatus] = useState<null | "saving" | "error">(null);

  const onSave = useCallback(
    async (next: string | null) => {
      setStatus("saving");
      try {
        await updateDataset(datasetId, { [field]: next });
        onSaved(next);
        setStatus(null);
      } catch {
        setStatus("error");
        showNotificationGlobal(
          `Failed to save ${label.toLowerCase()}`,
          "error",
        );
      }
    },
    [datasetId, field, label, onSaved],
  );

  return (
    <DescriptionSection
      description={value}
      canEdit={canEdit}
      onSave={(next) => void onSave(next)}
      saving={status === "saving"}
      error={status === "error"}
      label={label}
      icon={icon}
      addLabel={addLabel}
      placeholder={placeholder}
      pr={pr}
      collapsible
      defaultCollapsed
    />
  );
};
